import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Reveal, ImageReveal } from "@/components/Reveal";

const WhyPage = () => {
  return (
    <Layout>
      <div className="why-page">
      {/* Hero - Why We Exist */}
      <section className="why-tv-hero pt-32 pb-48 lg:pt-40 lg:pb-64 bg-purple">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal trigger="onLoad" delay={0.1} className="mx-auto mb-0">
              <p className="label bg-white text-purple opacity-100 px-3 py-1 inline-block normal-case">Why We Exist</p>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.2} width="full" className="mx-auto !overflow-visible">
              <div className="relative w-full">
                <div className="why-tv-hero-wordmark" aria-hidden="true">
                  Why We Exist
                </div>
                <div className="why-tv-image-set" aria-label="Old television showing static">
                <img
                  src="/assets/images/why/old_tv.png"
                  alt="Old television"
                  className="why-tv-image"
                  loading="eager"
                />
                <div className="why-tv-screen">
                  <div className="why-tv-noise" />
                  <div className="why-tv-scanlines" />
                  <h1 className="why-tv-title" data-text="The branding and marketing industry has a problem.">
                    The branding and marketing industry has <span>a problem.</span>
                  </h1>
                </div>
                </div>
              </div>
            </Reveal>

            <div className="why-tv-subtext max-w-3xl mx-auto text-left space-y-4 px-1 md:px-2 lg:px-0">
                <Reveal trigger="onLoad" delay={0.35}>
                  <p className="text-sm md:text-base text-white leading-relaxed font-light">
                    Strategy has been positioned as a luxury, reserved for large corporations with large budgets.
                  </p>
                </Reveal>
                <Reveal trigger="onLoad" delay={0.4}>
                  <p className="text-sm md:text-base text-white leading-relaxed font-light">
                    At the other end, “strategy” is reduced to tactics: more posts, more ads, more noise. Motion is confused with direction.
                  </p>
                </Reveal>
                <Reveal trigger="onLoad" delay={0.45}>
                  <p className="text-sm md:text-base text-[#c0ff09] leading-relaxed font-bold">
                    Nueva Media Solutions exists to close this gap...
                  </p>
                </Reveal>
                <Reveal trigger="onLoad" delay={0.5}>
                  <p className="text-sm md:text-base text-white leading-relaxed font-light">
                    We make strategy-led branding and marketing accessible to individuals, startups, and growing businesses, without inflated retainers, jargon, or decorative thinking.
                  </p>
                </Reveal>
                <Reveal trigger="onLoad" delay={0.55}>
                  <p className="text-sm md:text-base text-white leading-relaxed font-light">
                    We believe branding and marketing are one cohesive-system: positioning, message, identity, and execution aligned from the start.
                  </p>
                </Reveal>
                <Reveal trigger="onLoad" delay={0.65} className="mb-8">
                  <p className="text-sm md:text-base text-white leading-relaxed font-light">
                    Its not about doing things just for the sake of doing, rather <span className="text-[#c0ff09] font-bold">"Do What Matters."</span>
                  </p>
                </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="violet-glow-section border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch md:gap-0 relative z-10">
          {/* Column 1: Core Message & Problems List */}
          <div className="section-padding px-8 md:pl-16 md:pr-4 lg:pl-32 lg:pr-6 xl:pl-40 xl:pr-8 flex flex-col justify-center space-y-10">
            <div className="space-y-4">
              <Reveal trigger="onView" delay={0.1}>
                <p className="text-2xl md:text-3xl text-white/60 font-light leading-[0.95]">
                  Most brands are not failing because they lack effort.
                </p>
              </Reveal>
              <Reveal trigger="onView" delay={0.2}>
                <p className="text-2xl md:text-3xl text-white font-bold leading-[0.95]">
                  They're failing because they lack strategic clarity.
                </p>
              </Reveal>
              <Reveal trigger="onView" delay={0.3}>
                <div className="relative inline-block pb-2">
                  <p className="text-2xl md:text-3xl text-[#c0ff09] font-bold leading-[0.95]">
                    We exist to correct that.
                  </p>
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-2 text-[#c0ff09]"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2,8 Q50,0 98,8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </Reveal>
            </div>

            <div className="space-y-4">
              <Reveal trigger="onView" delay={0.4}>
                <p className="text-lg lg:text-xl font-bold text-white">
                  We solve the following problems:
                </p>
              </Reveal>
              <ul className="grid grid-cols-2 gap-4">
                {[
                  { lead: "Strategy ", bold: "reduced to trends" },
                  { lead: "Branding treated ", bold: "as simply aesthetics" },
                  { lead: "Marketing ", bold: "focused on noise" },
                  { lead: "Activity ", bold: "mistaken for progress" }
                ].map((text, i) => (
                  <Reveal key={i} delay={0.5 + (i * 0.1)} trigger="onView">
                    <li className="group aspect-square flex items-end rounded-[28px] border border-white/10 bg-white/5 p-5 lg:p-6 transition-all duration-300 hover:bg-[#c0ff09] hover:border-[#c0ff09]">
                      <p className="problem-card-text text-white/80 font-light leading-tight group-hover:text-black transition-colors">
                        {text.lead}<strong className="font-bold">{text.bold}</strong>
                      </p>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 2: Image */}
          <div className="relative min-h-[400px] h-full">
            <Reveal trigger="onView" delay={0.2} width="full" className="h-full">
              <img
                src="/assets/images/why/strategy_chess.png"
                alt="Strategy chess"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Standalone Belief Section */}
      <section className="section-padding bg-white border-t border-black/5">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-left space-y-12">
            <div className="space-y-6">
              <Reveal delay={0.1}>
                <p className="text-xl md:text-2xl text-muted-foreground font-light">
                  At Nueva Media Solutions we believe that
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <h2 className="display-md text-black !leading-[1.15]">
                  "If it's not strategically true, it won't work, no matter how good it looks."
                </h2>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="section-padding why-vision-section violet-glow-section border-t border-white/5">
        <div className="container-wide relative z-10">
          <div className="max-w-4xl mx-auto text-left">
            <Reveal className="mb-8" trigger="onLoad">
              <p className="label bg-white text-purple px-3 py-1 inline-block">OUR VISION</p>
            </Reveal>
            <Reveal className="mb-12" delay={0.1}>
              <h2 className="display-md text-white">
                We envision a culture where brands, personal and commercial, are built on strategy and clarity.
              </h2>
            </Reveal>

            <div className="grid lg:grid-cols-2 gap-12 items-center text-left">
              <div className="space-y-12">
                <div className="space-y-6">
                  <Reveal>
                    <p className="text-xl text-white/80 font-light">A world where:</p>
                  </Reveal>
                  <div className="space-y-4 pl-6">
                    {[
                      "Businesses grow by being authentic, not louder",
                      "Brands are grounded in belief, not tactics.",
                      "Strategy is a foundation, not an afterthought"
                    ].map((text, i) => (
                      <Reveal key={i} delay={i * 0.1}>
                        <div className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-white/50 rounded-full mt-2.5 shrink-0"></div>
                          <p className="text-lg text-white font-light">{text}</p>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  <Reveal>
                    <p className="text-xl text-white/80 font-light">Our vision is simple yet radical:</p>
                  </Reveal>
                  <div className="space-y-4 pl-6">
                    {[
                      "Help you become the “You’est You”",
                      "Free from pretense and drama",
                      "Free from unnecessary noise",
                      "Free from borrowed identities",
                      "Build brands on truth so growth becomes inevitable"
                    ].map((text, i) => (
                      <Reveal key={i} delay={0.3 + (i * 0.1)}>
                        <div className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-white/50 rounded-full mt-2.5 shrink-0"></div>
                          <p className="text-lg text-white font-light">{text}</p>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>

              <Reveal width="full" trigger="onLoad">
                <div className="flex justify-center items-center overflow-visible p-8">
                  <img
                    src="/assets/images/why/brand-circle.png"
                    alt="Brand Vision Circle"
                    className="w-full md:w-[120%] md:max-w-none h-auto object-contain md:transform md:scale-110"
                    loading="eager"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding why-cta-section violet-glow-section">
        <div className="container-narrow text-center relative z-10">
          <Reveal className="mx-auto mb-8 pb-2">
            <h2 className="display-md text-white">
              Ready to build your business with us?
            </h2>
          </Reveal>
          <Reveal className="mx-auto" delay={0.2}>
            <Button className="bg-white text-black hover:bg-[#c0ff09] hover:text-black rounded-lg px-8 py-6 text-lg font-bold transition-all duration-300" asChild>
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
      </div>
    </Layout>
  );
};

export default WhyPage;
